import { useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, whileStillBeingDescribed } from "../api.js";
import { Icon } from "../ui/icons.js";
import { PHOTO_GROUPS, photoGroup } from "../ui/photos.js";

/**
 * Your photos, the third view on Business (03 §business context): every picture the Studio can make a post
 * with, grouped the way its photo step groups them, and the place to add more before a post asks "which photos?".
 */
export function PhotosPanel() {
  const qc = useQueryClient();
  const file = useRef<HTMLInputElement>(null);
  const media = useQuery({ queryKey: ["media", "image"], queryFn: () => api.media("image"), refetchInterval: whileStillBeingDescribed });
  const upload = useMutation({ mutationFn: api.upload, onSuccess: () => void qc.invalidateQueries({ queryKey: ["media", "image"] }) });
  const library = media.data ?? [];
  // Under a heading that already says Your photos, the uploaded group is named for how it got here.
  const groups = PHOTO_GROUPS
    .map(([key, label]) => [key === "yours" ? "Uploaded by you" : label, library.filter((m) => photoGroup(m) === key)] as const)
    .filter(([, group]) => group.length);

  return (
    <section aria-label="Your photos" className="bg-panel border border-line rounded-[10px] px-4 py-3.5 flex flex-col gap-3.5">
      <div className="flex items-center gap-3">
        <span className="shrink-0 whitespace-nowrap text-[11px] font-semibold uppercase tracking-[.06em] text-muted">Your photos</span>
        <span className="text-[11.5px] text-muted">what your posts and videos are made with</span>
        <button onClick={() => file.current?.click()} disabled={upload.isPending}
          className="ml-auto shrink-0 px-2.5 py-1.5 rounded-lg border border-line-2 bg-panel text-xs font-medium hover:bg-page disabled:opacity-40 flex items-center gap-1.5">
          <Icon name="plus" size={12} /> {upload.isPending ? "Uploading…" : "Upload"}
        </button>
        <input ref={file} type="file" accept="image/png,image/jpeg,image/webp" multiple hidden aria-label="Photo files"
          onChange={(e) => { for (const f of Array.from(e.target.files ?? [])) upload.mutate(f); e.target.value = ""; }} />
      </div>

      {groups.map(([label, group]) => (
        <div key={label} className="flex flex-col gap-1.5">
          <h3 className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted flex">
            {label}<span className="ml-auto normal-case tracking-normal font-normal tabular-nums">{group.length}</span>
          </h3>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(112px,1fr))] gap-2">
            {group.map((m) => (
              <figure key={m.id} title={m.caption ?? ""} className="relative rounded-lg overflow-hidden aspect-[4/5] bg-line">
                <img src={m.thumbUrl} alt={m.caption ?? ""} loading="lazy" decoding="async" {...(m.width && m.height ? { width: m.width, height: m.height } : {})} className="w-full h-full object-cover" />
                {m.caption ? <figcaption className="absolute inset-x-0 bottom-0 px-1.5 py-1 bg-gradient-to-t from-black/70 to-transparent text-white text-[10px] truncate">{m.caption}</figcaption> : null}
              </figure>
            ))}
          </div>
        </div>
      ))}

      {!groups.length && !media.isLoading ? (
        <p className="text-[13px] text-muted leading-relaxed">
          No photos yet. Upload some, or give the chat your website and choose from what the lookup finds. A real photo of
          your own always beats a generated one.
        </p>
      ) : null}
      {upload.error ? <p className="text-bad text-xs">{upload.error.message}</p> : null}
    </section>
  );
}
